import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Types
export interface ModCreateProjectProps {
  open: boolean;
  onClose: () => void;
  onCreateProject?: (projectName: string) => void;
  onEditProject?: (projectId: number, projectName: string) => void; // Changed from string to number
  // Edit mode props
  editMode?: boolean;
  editProjectId?: number; // Changed from string to number
  editProjectName?: string;
}

// Styled Components
const ModalContainer = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  backgroundColor: "white",
  borderRadius: 8,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  outline: "none",
});

const ModalHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
});

const ModalContent = styled(Box)({
  padding: "0 20px 20px 20px",
});

const ModalFooter = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  padding: "16px 20px",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 6,
    "& fieldset": {
      borderColor: "#E0E0E0",
    },
    "&:hover fieldset": {
      borderColor: "#BDBDBD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#9E9E9E",
    },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "14px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9E9E9E",
    opacity: 1,
  },
});

const ActionButton = styled(Button)<{ disabled?: boolean }>(({ disabled }) => ({
  backgroundColor: disabled ? "#E0E0E0" : "#757575",
  color: disabled ? "#9E9E9E" : "white",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  padding: "8px 20px",
  borderRadius: 6,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: disabled ? "#E0E0E0" : "#616161",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#E0E0E0",
    color: "#9E9E9E",
  },
}));

const ModCreateProject: React.FC<ModCreateProjectProps> = ({
  open,
  onClose,
  onCreateProject,
  onEditProject,
  editMode = false,
  editProjectId,
  editProjectName = "",
}) => {
  const [projectName, setProjectName] = useState<string>("");

  useEffect(() => {
    if (open) {
      if (editMode && editProjectName) {
        setProjectName(editProjectName);
      } else {
        setProjectName("");
      }
    }
  }, [open, editMode, editProjectName]);

  const handleSubmit = () => {
    if (projectName.trim()) {
      if (editMode && editProjectId !== undefined) {
        onEditProject?.(editProjectId, projectName.trim());
      } else {
        onCreateProject?.(projectName.trim());
      }
      setProjectName("");
      onClose();
    }
  };

  const handleClose = () => {
    setProjectName("");
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && projectName.trim()) {
      handleSubmit();
    }
  };

  const modalTitle = editMode ? "Edit project" : "Create project";
  const buttonText = editMode ? "Save project" : "Create project";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="create-project-modal-title"
    >
      <ModalContainer id="megen-mod-create-project-container">
        <ModalHeader id="megen-mod-create-project-header">
          <Typography
            id="create-project-modal-title"
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#333",
            }}
          >
            {modalTitle}
          </Typography>
          <IconButton
            id="megen-mod-create-project-close-btn"
            onClick={handleClose}
            size="small"
            sx={{
              color: "#666",
              padding: "4px",
              "&:hover": {
                backgroundColor: "whitesmoke",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </ModalHeader>

        <ModalContent id="megen-mod-create-project-content">
          <StyledTextField
            id="megen-mod-create-project-name-input"
            fullWidth
            placeholder="Project name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onKeyPress={handleKeyPress}
            autoFocus
          />
        </ModalContent>

        <ModalFooter id="megen-mod-create-project-footer">
          <ActionButton
            id="megen-mod-create-project-submit-btn"
            variant="contained"
            disabled={!projectName.trim()}
            onClick={handleSubmit}
          >
            {buttonText}
          </ActionButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export default ModCreateProject;